import { string, mixed, object } from 'yup';
import type { TPlacementHostCompany } from '@shared/types/placement-host-company';

export type TImportHostFormValues = {
  file: File | null;
};

export const hostFormNames = {
  companyName: 'companyName',
  companyLogo: 'companyLogo',
  companyDescription: 'companyDescription',
  country: 'country',
  city: 'city',
  state: 'state',
  address1: 'address1',
  address2: 'address2',
  postalCode: 'postalCode',
  contactName: 'contactName',
  contactJobTitle: 'contactJobTitle',
  contactEmail: 'contactEmail',
  contactPhoneNumber: 'contactPhoneNumber',
  website: 'website',
} as const;

export type THostFormValues = {
  companyName: string;
  companyLogo?: unknown;
  companyDescription: string;
  country: string;
  city: string;
  state: string;
  address1: string;
  address2: string;
  postalCode: string;
  contactName: string;
  contactJobTitle: string;
  contactEmail: string;
  contactPhoneNumber: string;
  website: string;
};

export const createHostFormInitialValues = (
  hostData?: TPlacementHostCompany,
  tab: 'single' | 'import' = 'single'
): THostFormValues | TImportHostFormValues => {
  if (tab === 'import') {
    return { file: null } as TImportHostFormValues;
  }
  return {
    companyName: '',
    companyLogo: undefined,
    companyDescription: '',
    country: '',
    city: '',
    state: '',
    address1: '',
    address2: '',
    postalCode: '',
    contactName: '',
    contactJobTitle: '',
    contactEmail: '',
    contactPhoneNumber: '',
    website: '',
    ...createInitialValueFromExistingHost(hostData),
  } as THostFormValues;
};

export const importHostsFormSchema = object().shape({
  file: mixed()
    .required('File is required')
    .test('file-required', 'File is required', (value) => {
      return value !== null && value !== undefined;
    }),
});

const createInitialValueFromExistingHost = (
  hostData?: TPlacementHostCompany
) => {
  if (!hostData) return {};

  return {
    companyName: hostData.name,
    companyLogo: hostData.logo,
    companyDescription: hostData.description,
    country: hostData.country.code,
    city: hostData.city,
    state: hostData.state,
    address1: hostData.address_line_1,
    address2: hostData.address_line_2,
    postalCode: hostData.postal_code,
    contactName: hostData.contact_name,
    contactJobTitle: hostData.contact_job_title,
    contactEmail: hostData.contact_email,
    contactPhoneNumber: hostData.contact_phone_number,
    website: hostData.website,
  };
};

export const hostFormSchema = object().shape({
  companyName: string().required('Company name is required'),
  companyDescription: string().max(
    2000,
    'Description cannot exceed 2000 characters'
  ),
  companyLogo: mixed().nullable(),

  address1: string().required('Address is required'),
  address2: string().nullable(),
  city: string().required('City is required'),
  state: string().nullable(),
  country: string().required('Country is required'),
  postalCode: string().nullable(),
  contactName: string().nullable().optional(),
  contactJobTitle: string().nullable().optional(),
  contactEmail: string().email('Invalid email address').nullable().optional(),
  contactPhoneNumber: string().nullable().optional(),
  website: string().url('Invalid website URL').nullable().optional(),
});
